import { useCallback, useRef } from "react";

import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import { type CardInsertionOrder, InsertionSlot, type PlacedCard } from "@common/utils/redline/insertion-slot";
import { DIOPTRA_AUTHOR } from "@common/utils/redline/redline-engine";
import { type CardComment, type RedlinePayload, type RedlineTextProposal, textsPayloadFrom } from "@common/utils/redline/wire/redline-payload";
import { useAnalytics } from "../context/AnalyticsContext";
import { useAuthHeader } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { useReviewedDocument } from "../context/ReviewedDocumentContext";
import { anchorTrackers } from "../utils/anchor/anchor-tracker-registry";
import { ProposalApplier } from "../utils/anchor/proposal/proposal-applier";
import { DEFAULT_RECOVERY_CONFIG } from "../utils/anchor/recovery-config";
import { orderInsertion } from "../utils/api";
import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE, UserFacingError } from "../utils/errors";
import { ProposalEngineAccess } from "../utils/proposal-engine-access";
import type { RedlineUserGestureSurface } from "../utils/redline-apply-outcome";
import { type RelocatedCardComment, writeCardComments } from "../utils/redline-card-comments";
import { appliedCardsAfterWriteError, appliedEditReportOf, attemptedEditOf, tellAgentsCardsApplied } from "../utils/redline-applied-cards";
import { readLandingPageFacts } from "../utils/redline-landing-page-facts";
import { insertionAnchorsOfCard, officeSlotPage, orderedSlotInsertionsOf } from "../utils/redline-insertion-slot";
import { auditCardLandings, type CardLandingVerdict, type LandingTarget, logLandingMismatches } from "../utils/redline-landing-reconciliation";
import { useApplyRedlineOoxml, type UseApplyRedlineArgs } from "../utils/ooxml-recovery";
import { updateTableOfContentsFields } from "../utils/table-of-contents-fields";
import { lastApplyResultOfError, lastApplyResultOfOutcome, lastApplyResults } from "../components/chat/proposal-card/last-apply-results";

/**
 * `textRedline` is proposal engine only: the card's text, when the core emitted
 * a text card instead of markup. Required so every caller states it; null for a
 * markup card, which is applied from `ooxml`.
 */
export type ApplyRedlineArgs =
    Omit<UseApplyRedlineArgs, "applySource" | "origin">
    & { initiatedFrom: RedlineUserGestureSurface; textRedline: RedlineTextProposal | null; actionId: string | null; cardComments: CardComment[]; onCommentRelocated?: (relocatedCardComment: RelocatedCardComment) => void };

const TEXT_CARD_NEEDS_PROPOSAL_ENGINE_MESSAGE = "This proposal was prepared for the proposal engine, which is turned off for this account.";

const auditSingleLanding = async (landingTarget: LandingTarget): Promise<CardLandingVerdict | null> => {
    try {
        const [verdict] = await auditCardLandings([landingTarget], readLandingPageFacts);
        return verdict;
    } catch {
        return null;
    }
};

export function useApplyRedline(): (args: ApplyRedlineArgs) => Promise<boolean> {
    const applyUnrecorded = useApplyRedlineUnrecorded();

    return useCallback(
        async (args: ApplyRedlineArgs) => {
            const attemptedAt = new Date().toISOString();
            const commentRelocationSentences: string[] = [];
            const recordingArgs: ApplyRedlineArgs = {
                ...args,
                onCommentRelocated: (relocatedCardComment) => {
                    commentRelocationSentences.push(relocatedCardComment.relocationSentenceNamingTheComment);
                    args.onCommentRelocated?.(relocatedCardComment);
                },
            };
            const commentRelocationOfTheAttempt = (): string | null => (commentRelocationSentences.length === 0 ? null : commentRelocationSentences.join(" "));
            if (args.actionId !== null) lastApplyResults.beginAttempt(args.actionId);
            try {
                const applied = await applyUnrecorded(recordingArgs);
                if (args.actionId !== null) lastApplyResults.record(args.actionId, { ...lastApplyResultOfOutcome(applied, attemptedAt, args.initiatedFrom), commentRelocation: commentRelocationOfTheAttempt() });
                return applied;
            } catch (error) {
                if (args.actionId !== null) lastApplyResults.record(args.actionId, { ...lastApplyResultOfError(error, attemptedAt, args.initiatedFrom), commentRelocation: commentRelocationOfTheAttempt() });
                throw error;
            }
        },
        [applyUnrecorded],
    );
}

function useApplyRedlineUnrecorded(): (args: ApplyRedlineArgs) => Promise<boolean> {
    const applyRedlineOoxml = useApplyRedlineOoxml();
    const { currentReviewId } = useReviewedDocument();
    const { sendError } = useAnalytics();
    const { messagesHistory, selectedConversationId } = useChat();
    const authHeader = useAuthHeader();
    const chatRef = useRef({ messagesHistory, selectedConversationId, authHeader });
    chatRef.current = { messagesHistory, selectedConversationId, authHeader };

    return useCallback(
        async ({ initiatedFrom, ...args }: ApplyRedlineArgs) => {
            const refreshTableOfContentsAfterApply = async (writtenPackageOoxmls: readonly string[]): Promise<void> => {
                try {
                    await updateTableOfContentsFields(writtenPackageOoxmls);
                } catch (error) {
                    sendError(error instanceof Error ? error : new Error(String(error)), { component: "useApplyRedline", action: "update_table_of_contents" });
                }
            };
            const reportSlotFailure = (failure: unknown): void => {
                sendError(failure instanceof Error ? failure : new Error(String(failure)), { component: "useApplyRedline", action: "place_insertions_in_slot" });
            };
            const cardInsertionOrderOf = async (cardId: string, anchorBookmarks: string[]): Promise<CardInsertionOrder | null> => {
                const chat = chatRef.current;
                const conversationId = chat.selectedConversationId;
                if (conversationId === undefined) return null;
                const signedInHeader = chat.authHeader;
                const slotInsertions = await orderedSlotInsertionsOf({
                    conversationId,
                    turns: chat.messagesHistory,
                    anchorBookmarks,
                    page: officeSlotPage,
                    orderInsertion: signedInHeader === null ? null : (request) => orderInsertion(signedInHeader, request),
                    reportFailure: (_cardId, failure) => reportSlotFailure(failure),
                });
                return { cardId, slotInsertions };
            };
            const placeParagraphsThenTables = async (card: RedlineTextProposal, bookmarkNames: string[], order: CardInsertionOrder): Promise<PlacedCard> => {
                const withParagraphsPlaced = await InsertionSlot.placeCardParagraphs({ card, bookmarkNames, order, page: officeSlotPage });
                return { ...withParagraphsPlaced, card: await InsertionSlot.placeCardTables({ card: withParagraphsPlaced.card, order, page: officeSlotPage }) };
            };
            const placedInSlot = async (textRedline: RedlineTextProposal, bookmarkNames: string[]): Promise<PlacedCard> => {
                const unplaced = { card: textRedline, bookmarkNames };
                const actionId = args.actionId;
                const anchorBookmarks = insertionAnchorsOfCard(textRedline);
                const cardAddsNoBlockInAnySlot = actionId === null || anchorBookmarks.length === 0;
                if (cardAddsNoBlockInAnySlot) return unplaced;
                try {
                    const order = await cardInsertionOrderOf(actionId, anchorBookmarks);
                    if (order === null) return unplaced;
                    return await placeParagraphsThenTables(textRedline, bookmarkNames, order);
                } catch (failure) {
                    reportSlotFailure(failure);
                    return unplaced;
                }
            };
            const markupBookmarkNamesInSlot = async (bookmarkNames: string[]): Promise<string[]> => {
                const actionId = args.actionId;
                if (actionId === null || args.insertAfter !== true || bookmarkNames.length !== 1) return bookmarkNames;
                const anchorBookmark = bookmarkNames[0];
                try {
                    const order = await cardInsertionOrderOf(actionId, [anchorBookmark]);
                    return order === null ? bookmarkNames : [await InsertionSlot.markupInsertionAnchorOf({ anchorBookmark, order, page: officeSlotPage })];
                } catch (failure) {
                    reportSlotFailure(failure);
                    return bookmarkNames;
                }
            };
            const applyWithProposalEngine = async (authorName: string | null): Promise<boolean> => {
                const placed = args.textRedline === null ? null : await placedInSlot(args.textRedline, args.bookmarks ?? []);
                const bookmarkNames = placed === null ? await markupBookmarkNamesInSlot(args.bookmarks ?? []) : placed.bookmarkNames;
                /**
                 * CASES, each authored by the signed-in user
                 *   - the card carries texts  -> apply those
                 *   - the card carries markup -> apply the markup
                 */
                const signedInAuthor = authorName ?? DIOPTRA_AUTHOR;
                const redline: RedlinePayload = placed !== null
                    ? { ...textsPayloadFrom(placed.card), author: signedInAuthor }
                    : { kind: "ooxml", redlineOoxml: args.ooxml, author: signedInAuthor };
                const request = { bookmarkNames, redline, cardId: args.actionId };
                const applier = new ProposalApplier(anchorTrackers, DEFAULT_RECOVERY_CONFIG);
                const isConflictResolutionOfApplyAll = initiatedFrom === "chat_apply_all" && args.actionId === null;
                const attemptedEdit = attemptedEditOf(args.actionId, bookmarkNames, placed === null ? null : placed.card);
                /**
                 * WHY
                 *   "in_progress" is the merge-repair status: the cards label it "Auto-repairing
                 *   redline…". The proposal engine never repairs, so it reports only the outcome;
                 *   the card's own applying state covers the wait.
                 */
                let writtenPackageOoxmls: readonly string[];
                try {
                    /**
                     * CASES
                     *   - args.insertAfter is true   -> insert after the bookmarked range
                     *   - args.insertAfter is false  -> apply on the bookmarked range itself
                     */
                    writtenPackageOoxmls = args.insertAfter ? [(await applier.insertAfterBookmarks(request)).packageOoxml] : (await applier.applyOnBookmarks(request)).writtenPackageOoxmls;
                } catch (error) {
                    /**
                     * WHEN apply failed because the bookmark location cannot be found
                     *   CONSEQUENCE
                     *     Return false instead of rethrowing.
                     *     The caller sees a recoverable apply miss and decides what to do next.
                     */
                    if (error instanceof ApplyFailureError && error.reason === APPLY_FAILURE_LOCATION_NOT_FOUND) {
                        args.onStatus?.("failed");
                        return false;
                    }
                    args.onStatus?.("failed");
                    if (!isConflictResolutionOfApplyAll) tellAgentsCardsApplied(appliedCardsAfterWriteError(attemptedEdit, error));
                    throw error;
                }
                const landingTarget: LandingTarget = placed !== null
                    ? { kind: "text_card", ruleId: "single", bookmarkNames, card: placed.card, insertsAfterBookmarks: args.insertAfter === true }
                    : { kind: "markup", ruleId: "single", bookmarkNames, packageOoxmls: [args.ooxml], insertsAfterBookmarks: args.insertAfter === true };
                const verdict = await auditSingleLanding(landingTarget);
                if (verdict !== null) logLandingMismatches([verdict], [landingTarget]);
                if (!isConflictResolutionOfApplyAll) tellAgentsCardsApplied([appliedEditReportOf(attemptedEdit, false)]);
                args.onStatus?.("success");
                await refreshTableOfContentsAfterApply(writtenPackageOoxmls);
                await writeCardComments(args.actionId, args.cardComments, placed === null ? args.textRedline : placed.card, (relocatedCardComment) => args.onCommentRelocated?.(relocatedCardComment), sendError);
                return true;
            };

            const access = await ProposalEngineAccess.current();
            const bookmarkCount = args.bookmarks?.length ?? 0;
            const takesProposalEngine = access.enabled && bookmarkCount > 0;
            /**
             * CASES
             *   - proposal engine enabled and bookmarks exist  -> use bookmark-based proposal apply
             *   - otherwise                                    -> fall back to OOXML apply
             */
            if (takesProposalEngine) return applyWithProposalEngine(access.authorName);
            /**
             * WHEN the card carries texts but the engine is off for this account
             *   CONSEQUENCE
             *     There is no markup to insert the old way; tell the user instead of writing nothing.
             */
            if (args.textRedline !== null) throw new UserFacingError(TEXT_CARD_NEEDS_PROPOSAL_ENGINE_MESSAGE);
            return applyRedlineOoxml({
                ...args,
                applySource: { initiatedFrom, reviewId: currentReviewId },
                origin: "pipeline",
            });
        },
        [applyRedlineOoxml, currentReviewId, sendError],
    );
}

export function useApplyRedlineOrFail(): (args: ApplyRedlineArgs) => Promise<void> {
    const applyRedline = useApplyRedline();

    return useCallback(
        async (args: ApplyRedlineArgs) => {
            const applied = await applyRedline(args);
            /**
             * WHEN applyRedline returned false
             *   CONSEQUENCE
             *     Throw APPLY_FAILURE_LOCATION_NOT_FOUND instead of returning quietly.
             */
            if (!applied) {
                throw new ApplyFailureError(
                    CANNOT_LOCATE_TARGET_MESSAGE,
                    APPLY_FAILURE_LOCATION_NOT_FOUND,
                );
            }
        },
        [applyRedline],
    );
}
